/* Copyright (c) 2020-present CloudIO Inc */

import {
  BOOLEAN_OPS_KEY_TYPE,
  DataRecord,
  OPS_ICONS,
  TF,
  YN,
  YN_TF_OPS_KEY_TYPE,
} from '@cloudio-saas/datasource-types';
import {
  Box,
  IconButton,
  InputAdornment,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
} from '@mui/material';
import { BaseProps, Form, isNotEmpty, SearchCheckbox } from 'componentregistry';
import {
  useAttributeDefinition,
  useCurrentDataStore,
} from 'components/DataStoreReference/DataStoreReference';
import { HorizontalFormField } from 'components/Form/FormUtils';
import { allYNTFOps, hasYNTFEditor } from 'components/SmartSearch/Operators';
import {
  getName,
  getOperator,
  getOpOptions,
  getValue,
} from 'components/SmartSearch/SearchUtils';
import { FieldWrapper, useFieldVariant } from 'components/TextField/BaseField';
import {
  Option,
  SelectInputProps,
  VirtualizedSelectInput,
} from 'custom/SelectInput';
import { useFocus } from 'hooks/useFocus';
import Icon, { parseIconText } from 'icons/Icon';
import React, { useEffect, useId, useState } from 'react';
import { useStoreAttributeFilter } from 'store/useAppStore';
import { LookupOption } from 'store/utils/utils';
import { useTranslate } from '../../utils/lang';
import { useStringExpression } from 'expression/useExpression';
import zIndex from 'constants/ZIndex';

const lkpOptions = [
  {
    value: 'Y',
    label: 'Checked',
    icon: 'check-square',
    color: 'primary',
  },
  {
    value: 'N',
    label: 'Unchecked',
    icon: 'square',
  },
];

interface CheckboxProps
  extends Required<SearchCheckbox.SearchCheckbox>,
    BaseProps<'SearchCheckbox'> {
  rowIndex?: number;
  headerFilter?: boolean;
  onHeaderFilterSearch?: () => void;
}

interface Props extends CheckboxProps, Form.HorizontalFormProps {}

function IOCheckbox(
  {
    appUid,
    autoFocus,
    className,
    defaultOperator,
    domProps,
    flex,
    formHorizontal,
    helperText,
    hideOperatorChooser,
    itemId,
    label,
    labelAlignment,
    labelWidth,
    required,
    viewAttribute,
    platform,
    placeholder,
    headerFilter,
    pageId,
    onHeaderFilterSearch,
  }: Props,
  ref: React.Ref<HTMLDivElement>,
): React.ReactElement {
  const { t } = useTranslate();
  helperText = t(helperText);
  let labelResult =
    useStringExpression({
      exp: label,
      context: {
        appUid,
        pageId,
        itemId,
        property: 'label',
        data: {},
      },
    }) || undefined;
  placeholder = t(placeholder);
  labelResult = t(labelResult);

  const attribute = useAttributeDefinition(viewAttribute);
  const bool = attribute?.attributeType === 'Boolean';
  const variant = useFieldVariant(appUid);
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
    if (platform.isRecording()) {
      platform.recordLine(
        itemId,
        `await ui.click({ testid: '${itemId}-op' });`,
      );
    }
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const [ops, setOps] = useState<YN_TF_OPS_KEY_TYPE>(defaultOperator);
  const [value, setValue] = useState<YN | ''>('');

  const options = getOpOptions('YN', t);

  const store = useCurrentDataStore<DataRecord>();
  const filter = useStoreAttributeFilter<DataRecord>(store, viewAttribute);

  useEffect(() => {
    if (filter) {
      let op = getOperator(filter) as YN_TF_OPS_KEY_TYPE | BOOLEAN_OPS_KEY_TYPE;
      op = op === 'isTrue' ? 'is' : op;
      setOps(op);
      const _hasEditor = hasYNTFEditor(op);
      if (_hasEditor) {
        const val = getValue(filter) as TF | YN | boolean;
        if (val === 'Y' || val === 'T' || val === true) {
          setValue('Y');
        } else {
          setValue('N');
        }
      } else {
        setValue('');
      }
    } else {
      // #974 Fix. Any filter other than values has to be set to default operator
      setOps(defaultOperator);
      setValue('');
    }
  }, [defaultOperator, filter]);

  const hasEditor = hasYNTFEditor(ops);

  function updateFilter(attr: string, op: YN_TF_OPS_KEY_TYPE, val: YN | '') {
    if (!store) return;

    let filters = store.getSmartSearchFilter();
    filters = filters.filter((f) => getName(f) !== attr);
    const _hasEditor = hasYNTFEditor(op);

    if (!_hasEditor || isNotEmpty(val)) {
      const v: string | boolean = _hasEditor
        ? bool
          ? val === 'Y'
          : attribute?.attributeType === 'TF'
          ? val === 'Y'
            ? 'T'
            : 'F'
          : val
        : bool
        ? true
        : '';
      const _filter = {
        [attr]: { [bool ? (op === 'is' ? 'isTrue' : op) : op]: v },
      };
      filters.push(_filter);
    } else {
      setOps(op);
      setValue('');
    }

    store?.setSmartSeachFilter(filters);
  }

  const record = lkpOptions.find((r) => r.value === value);
  const { iconName, iconType } = parseIconText(record?.icon);

  const inputRef = useFocus(itemId);
  const id = useId();

  const selectFieldProps: SelectInputProps<Option> = {
    id,
    autoFocus,
    value,
    variant,
    required,
    onChange: (changedVal: string, option: Option | null) => {
      updateFilter(viewAttribute, ops, changedVal as YN);
      if (platform.isRecording() && option && !headerFilter) {
        platform.recordLine(
          itemId,
          `await ui.type('${itemId}', '${option.label}');`,
        );
      }
    },
    IOInputProps: {
      inputRef,
      startAdornment: (
        <InputAdornment position="start">
          {hideOperatorChooser ? undefined : (
            <IconButton
              data-testid={`${itemId}-op`}
              onClick={handleClick}
              data-tip={allYNTFOps[ops] || ops}
            >
              <Icon iconName={OPS_ICONS[ops] || 'equals'} />
            </IconButton>
          )}
          {record && iconName ? (
            <Icon
              iconName={iconName}
              iconType={iconType}
              color={record.color}
              className="icon"
            />
          ) : null}
        </InputAdornment>
      ),
    },
    valueAttribute: 'value',
    labelAttribute: 'label',
    options: lkpOptions,
    placeholder: hasEditor ? placeholder : allYNTFOps[ops],
    disabled: !hasEditor,
    sx: { width: headerFilter ? '200px' : undefined },
    renderOption: (liprops, option) => {
      const row = option as LookupOption;
      const icon = parseIconText(row.icon);
      return (
        <Box
          component="li"
          sx={{ '& > .icon': { mr: 2, flexShrink: 0 } }}
          {...liprops}
        >
          <>
            {icon.iconName ? (
              <Icon
                iconName={icon.iconName}
                iconType={icon.iconType}
                color={row.color}
                className="icon"
              />
            ) : null}
            {row.label}
          </>
        </Box>
      );
    },
  };

  const control =
    formHorizontal && !headerFilter ? (
      <VirtualizedSelectInput {...selectFieldProps} />
    ) : (
      <VirtualizedSelectInput
        {...selectFieldProps}
        label={headerFilter ? undefined : labelResult}
        helpText={helperText}
      />
    );

  return (
    <>
      <FieldWrapper
        className={className}
        domProps={domProps}
        flex={flex}
        ref={ref}
      >
        {formHorizontal && !headerFilter ? (
          <HorizontalFormField
            control={control}
            helperText={helperText}
            label={labelResult}
            labelAlignment={labelAlignment}
            labelWidth={labelWidth}
            required={required}
            id={id}
          />
        ) : (
          control
        )}
      </FieldWrapper>
      <Menu
        id="basic-menu"
        anchorEl={anchorEl}
        open={!!anchorEl}
        onClose={handleClose}
        MenuListProps={{
          'aria-labelledby': 'basic-button',
        }}
      >
        {options.map((option) => {
          return (
            <MenuItem
              data-testid={`op-${option.value}`}
              key={option.value}
              onClick={() => {
                const op = option.value as YN_TF_OPS_KEY_TYPE;
                setOps(op);
                handleClose();
                updateFilter(viewAttribute, op, value);
                if (hasYNTFEditor(op)) {
                  setTimeout(() => {
                    inputRef.current?.focus();
                  }, 1);
                } else {
                  onHeaderFilterSearch?.();
                }
              }}
              sx={{ zIndex: zIndex.Tooltip + 1 }}
            >
              <ListItemIcon>
                <Icon iconName={OPS_ICONS[option.value] || 'equals'} />
              </ListItemIcon>
              <ListItemText>{option.label}</ListItemText>
            </MenuItem>
          );
        })}
      </Menu>
    </>
  );
}

export default React.forwardRef(IOCheckbox);
